// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent } from 'react';
import { AppIcon } from '@loa/ui-components';

type MaxFundingScenarioTabsProps = Readonly<{
  retirementAges: number[];
  /** Per-tab custom names (aligned by index). `undefined` falls back to "Calculation N". */
  names?: Array<string | undefined>;
  activeIndex: number;
  onSelect: (index: number) => void;
  onClose: (index: number) => void;
  onAdd: () => void;
  /** Commits a renamed tab label into parent state. Pass `undefined` to clear the custom name. */
  onRename?: (index: number, name: string | undefined) => void;
  disabled?: boolean;
}>;

function defaultTabLabel(idx: number): string {
  return `Calculation ${idx + 1}`;
}

export function MaxFundingScenarioTabs({
  retirementAges,
  names,
  activeIndex,
  onSelect,
  onClose,
  onAdd,
  onRename,
  disabled = false,
}: MaxFundingScenarioTabsProps) {
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (editingIdx !== null && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingIdx]);

  const startEdit = (idx: number) => {
    if (disabled || !onRename) return;
    setDraft(names?.[idx] ?? defaultTabLabel(idx));
    setEditingIdx(idx);
  };

  const commit = (idx: number) => {
    const trimmed = draft.trim();
    const fallback = defaultTabLabel(idx);
    // Empty or equal-to-default → clear any custom name (revert to default label).
    const nextName = trimmed === '' || trimmed === fallback ? undefined : trimmed;
    if (onRename && nextName !== names?.[idx]) onRename(idx, nextName);
    setEditingIdx(null);
  };

  const cancel = () => {
    setEditingIdx(null);
  };

  const handleKey = (e: KeyboardEvent<HTMLInputElement>, idx: number) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commit(idx);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  return (
    <div className="flex items-center gap-md flex-wrap" data-testid="max-funding-tabs">
      {retirementAges.map((age, idx) => {
        const isActive = idx === activeIndex;
        const canClose = retirementAges.length > 1;
        const isEditing = editingIdx === idx;
        const label = names?.[idx]?.trim() || defaultTabLabel(idx);
        return (
          <div
            // Tab row contains a nested close-button child, so this row can't
            // itself be a native <button> (HTML disallows nested buttons).
            // Keep div + role=button + tabIndex + keyDown to provide
            // keyboard activation while leaving the inner button intact.
            key={`${age}-${idx}`}
            role="button"
            tabIndex={0}
            className={`flex items-center gap-[10px] px-md py-[10px] rounded-lg cursor-pointer border transition-colors ${
              isActive
                ? 'bg-purple-primary border-purple-primary text-white'
                : 'bg-white border-[#E1E1E1] text-grey-04 hover:bg-[#F5F5F7]'
            }`}
            onClick={() => {
              if (isEditing) return;
              onSelect(idx);
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              startEdit(idx);
            }}
            onKeyDown={(e) => {
              if (isEditing) return;
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(idx);
              }
            }}
            data-testid={`scenario-tab-${idx}`}
          >
            {isEditing ? (
              <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => handleKey(e, idx)}
                onBlur={() => commit(idx)}
                onClick={(e) => e.stopPropagation()}
                className={`font-sans text-body-02 font-normal leading-normal bg-transparent outline-none border-b min-w-[120px] ${
                  isActive ? 'text-white border-white' : 'text-black border-grey-04'
                }`}
                data-testid={`scenario-tab-input-${idx}`}
                maxLength={60}
              />
            ) : (
              <span className="font-sans text-body-02 font-normal leading-normal">
                {label}
              </span>
            )}
            {canClose && !isEditing && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled) return;
                  onClose(idx);
                }}
                className="flex items-center justify-center w-[24px] h-[24px] bg-transparent border-none cursor-pointer p-0"
                aria-label={`Close ${label}`}
                data-testid={`scenario-tab-close-${idx}`}
              >
                <AppIcon name="close" size={14} color={isActive ? '#FFFFFF' : '#525252'} />
              </button>
            )}
          </div>
        );
      })}
      <button
        type="button"
        onClick={onAdd}
        disabled={disabled}
        className="flex items-center justify-center p-[10px] bg-transparent border-none cursor-pointer disabled:opacity-50"
        aria-label="Add retirement age"
        data-testid="scenario-add-tab"
      >
        <AppIcon name="figma plus square" size={16} color="#141414" />
      </button>
    </div>
  );
}
